import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { downloadFromApi } from "../../lib/download";
import {
  getAttendanceImportYears, attendanceImportPreview, attendanceImportCommit,
  getAssessmentImportYears, assessmentImportPreview, assessmentImportCommit,
} from "../../lib/api";

const ATTENDANCE_CONFIG = {
  key: "attendance",
  noun: "attendance",
  templatePath: "/api/attendance-import/template",
  templateName: "historical_attendance_template.csv",
  getYears: getAttendanceImportYears,
  preview: attendanceImportPreview,
  commit: attendanceImportCommit,
  info: (
    <>
      <strong>Columns:</strong> student_id, student_name, date (mm/dd/yyyy), attendance_status.
      <div className="mt-1 text-xs">
        Accepted statuses: <code>present</code>, <code>absent_excused</code>, <code>absent_unexcused</code>,{" "}
        <code>tardy</code>, <code>left_early</code>. Rows matched by name (missing student_id) are flagged for review.
      </div>
    </>
  ),
  matchedHeaders: ["Row", "Student", "Date", "Status", "Match", ""],
  matchedRow: (m) => [
    m.row,
    `${m.student_name}${m.student_number ? ` (${m.student_number})` : ""}`,
    m.date,
    m.status,
    m.matched_by === "name" ? <Badge key="mb" variant="outline" className="text-amber-700 border-amber-300">by name</Badge> : "by id",
    m.duplicate ? <span key="d" className="text-amber-700 text-xs">skip: {m.reason}</span> : <span key="ok" className="text-emerald-700 text-xs">import</span>,
  ],
};

const ASSESSMENT_CONFIG = {
  key: "assessment",
  noun: "assessment",
  templatePath: "/api/assessment-import/template",
  templateName: "historical_assessment_template.csv",
  getYears: getAssessmentImportYears,
  preview: assessmentImportPreview,
  commit: assessmentImportCommit,
  info: (
    <>
      <strong>Columns:</strong> student_id, student_name, date (mm/dd/yyyy), class_name, group_type,{" "}
      participation_level, engagement, communication, behavior, attention, work_completion, notes, school_year.
      <div className="mt-1 text-xs">
        Each category score is <code>1</code>–<code>5</code> (leave blank if not assessed; at least one is required).
        Rows matched by name (missing student_id) are flagged for review.
      </div>
    </>
  ),
  matchedHeaders: ["Row", "Student", "Date", "Class", "Scores", "Match", ""],
  matchedRow: (m) => [
    m.row,
    `${m.student_name}${m.student_number ? ` (${m.student_number})` : ""}`,
    m.date,
    m.class_name,
    m.scores || "—",
    m.matched_by === "name" ? <Badge key="mb" variant="outline" className="text-amber-700 border-amber-300">by name</Badge> : "by id",
    m.duplicate ? <span key="d" className="text-amber-700 text-xs">skip: {m.reason}</span> : <span key="ok" className="text-emerald-700 text-xs">import</span>,
  ],
};

export default function HistoricalImportCard() {
  return (
    <Card data-testid="historical-import-card">
      <CardHeader>
        <CardTitle>Import Historical Data</CardTitle>
        <CardDescription>
          Bulk-import past-year records from a CSV. Imported records appear in graphs and reports
          exactly like manually entered data.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="attendance">
          <TabsList className="mb-4" data-testid="historical-import-tabs">
            <TabsTrigger value="attendance" data-testid="historical-import-tab-attendance">Attendance</TabsTrigger>
            <TabsTrigger value="assessment" data-testid="historical-import-tab-assessment">Assessments</TabsTrigger>
          </TabsList>
          <TabsContent value="attendance">
            <ImportPanel config={ATTENDANCE_CONFIG} />
          </TabsContent>
          <TabsContent value="assessment">
            <ImportPanel config={ASSESSMENT_CONFIG} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function ImportPanel({ config }) {
  const [years, setYears] = useState([]);
  const [year, setYear] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const tid = `${config.key}-import`;

  useEffect(() => {
    config.getYears()
      .then((r) => { setYears(r.data || []); if ((r.data || []).length) setYear(r.data[0]); })
      .catch(() => {});
  }, [config]);

  const handleTemplate = async () => {
    try {
      const path = year ? `${config.templatePath}?school_year=${encodeURIComponent(year)}` : config.templatePath;
      await downloadFromApi(path, config.templateName);
    } catch { toast.error("Failed to download template"); }
  };

  const onFile = (e) => {
    setFile(e.target.files?.[0] || null);
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!year) { toast.error("Select a school year"); return; }
    if (!file) { toast.error("Choose a CSV file"); return; }
    setLoading(true);
    try {
      const res = await config.preview(file, year);
      setPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  const handleCommit = async () => {
    if (!file || !year) return;
    setCommitting(true);
    try {
      const res = await config.commit(file, year);
      const d = res.data;
      toast.success(`Imported ${d.inserted} record(s) into ${d.school_year}. Skipped ${d.skipped_duplicates} duplicate(s).`);
      setPreview(null);
      setFile(null);
      const input = document.getElementById(`${tid}-file`);
      if (input) input.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setCommitting(false); }
  };

  const s = preview?.summary;

  return (
    <div className="space-y-4" data-testid={`${tid}-panel`}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">School Year</label>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-40" data-testid={`${tid}-year`}>
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={y} data-testid={`${tid}-year-${y}`}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={handleTemplate} data-testid={`${tid}-template-btn`}>
          Download CSV Template
        </Button>
      </div>

      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
        {config.info}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          id={`${tid}-file`}
          type="file"
          accept=".csv,text/csv"
          onChange={onFile}
          className="text-sm"
          data-testid={`${tid}-file`}
        />
        <Button onClick={handlePreview} disabled={loading || !file} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid={`${tid}-preview-btn`}>
          {loading ? "Analyzing…" : "Preview Import"}
        </Button>
      </div>

      {preview && (
        <div className="space-y-4" data-testid={`${tid}-preview`}>
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-emerald-600" data-testid={`${tid}-count-import`}>{s.to_import} ready to import</Badge>
            <Badge variant="outline" className="text-amber-700 border-amber-300" data-testid={`${tid}-count-dupes`}>{s.duplicates} duplicate(s) — skipped</Badge>
            <Badge variant="outline" className="text-blue-700 border-blue-300" data-testid={`${tid}-count-name`}>{s.name_matched} matched by name</Badge>
            <Badge variant="outline" className="text-red-700 border-red-300" data-testid={`${tid}-count-unmatched`}>{s.unmatched} unmatched</Badge>
            <Badge variant="outline" className="text-red-700 border-red-300" data-testid={`${tid}-count-errors`}>{s.errors} error(s)</Badge>
          </div>

          {preview.matched?.length > 0 && (
            <PreviewTable
              title="Matched rows"
              testid={`${tid}-matched`}
              headers={config.matchedHeaders}
              rows={preview.matched.map(config.matchedRow)}
            />
          )}
          {preview.unmatched?.length > 0 && (
            <PreviewTable
              title="Unmatched (not imported)"
              testid={`${tid}-unmatched`}
              headers={["Row", "student_id", "Name", "Date", "Reason"]}
              rows={preview.unmatched.map((u) => [u.row, u.student_id || "—", u.student_name || "—", u.date, u.reason])}
            />
          )}
          {preview.errors?.length > 0 && (
            <PreviewTable
              title="Errors (not imported)"
              testid={`${tid}-errors`}
              headers={["Row", "Name", "Date", "Problem"]}
              rows={preview.errors.map((e) => [e.row, e.student_name || "—", e.date, (e.messages || []).join("; ")])}
            />
          )}

          <div className="flex items-center gap-3 pt-2">
            <Button
              onClick={handleCommit}
              disabled={committing || !s.to_import}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid={`${tid}-confirm-btn`}
            >
              {committing ? "Importing…" : `Confirm & Import ${s.to_import} Record(s)`}
            </Button>
            <span className="text-xs text-muted-foreground">Existing {config.noun} records are never overwritten.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewTable({ title, testid, headers, rows }) {
  return (
    <div>
      <h4 className="text-sm font-semibold mb-1">{title} <span className="text-muted-foreground font-normal">({rows.length})</span></h4>
      <div className="max-h-64 overflow-auto border rounded-lg">
        <table className="w-full text-sm" data-testid={testid}>
          <thead className="sticky top-0 bg-gray-50">
            <tr className="text-left border-b">{headers.map((h) => <th key={h} className="p-2 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b hover:bg-gray-50">
                {r.map((c, j) => <td key={j} className="p-2 align-top">{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
